import React, { useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { SignInButton } from "../components/SignInButton";

const Home: React.FC = () => {
	const { accounts } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

	const faqs = [
		{
			question: "How do I create a PO?",
			answer:
				"To create a new purchase order, click the <b>Create PO</b> button either from the home page or the navigation bar. Fill in the details. Required fields are marked with a red asterisk (*) and shown in yellow in the screenshot below.<br><br>If you don't know which department to select, please consult with your supervisor. When a department is selected, it will auto-fill the same department on the line level.<br>If you don't know which account to select, please consult with the accounting team.<br><br>You can add up to 10 attachments to the PO to include quotes, and other documents.<br>You can add line items by clicking the <b>Add Empty Expense Line</b> button.<br>You can copy an existing line item by clicking on the <b>Copy this Expense Line</b> button.<br>Only once all details are filled in, the <b>Create Purchase Order</b> button will enable and you can submit it.",
			imageUrl: "/images/faq-create.png",
		},
		{
			question: "How do I edit a PO?",
			answer:
				'Only purchase orders in \"Pending Approval\" status that are not yet approved can be edited. To edit, go to the PO List page and <b>click on the PO Number</b> of the purchase order you want to edit. This opens the PO details page where you can make changes and re-submit the PO for approval.',
			imageUrl: "/images/faq-edit.png",
		},
		{
			question: "Can I copy an existing PO?",
			answer:
				'Yes! On the PO List, click the \"Copy PO\" icon at the end of the PO line. This creates a new PO with the same vendor, department and line item details. The Amount and Memo fields are not copied over to allow for changes in the new PO.',
			imageUrl: "/images/faq-copy.png",
		},
		{
			question: "How do I print/download a PDF of the PO?",
			answer:
				'On the PO List, click the \"Download PDF\" icon <b>next to the PO Number</b>. This automatically downloads a PDF of the purchase order. Only Purchase Orders with document status "Approved" can be downloaded as PDF.',
			imageUrl: "/images/faq-download.png",
		},
		{
			question: "How do I receive a PO?",
			answer:
				'On the PO List, expand the Purchase Order you want to receive using the toggle. Click the "Receive PO" button. On the modal dialog, enter the Receive Date and optional Memo. Select the line items you want to receive. By default all line items are selected. You can deselect/select all items using the \"Uncheck All\" / \"Check all\" toggle button. Items already received are disabled with a gray background. Click "Receive PO". Only Purchase Orders with document status \"Pending Receipt\" or \"Pending Billing/Partially Received\" can be received.',
			imageUrl: "/images/faq-receive-po.png",
		},
		{
			question: "Can I approve Purchase Orders?",
			answer:
				'Yes! If you are the \"Next Approver\" on a purchase order, you can use Puff Relay to approve it. Click the <b>POs requiring my approval</b> toggle to view a list of purchase orders that require your approval. On the PO List, expand the Purchase Order you want to approve using the toggle. Click the <b>Approve</b> button.',
			imageUrl: "/images/faq-approve-po.png",
		},
		{
			question: "Can I reject Purchase Orders?",
			answer:
				'Yes! If you are the \"Next Approver\" on a purchase order, you can use Puff Relay to reject it. Click the <b>POs requiring my approval</b> toggle to view a list of purchase orders that require your approval. On the PO List, expand the Purchase Order you want to reject using the toggle. Clicking the <b>Reject</b> button will open a dialog where you can enter an optional rejection reason. Click the <b>Reject</b> button to reject the PO.',
			imageUrl: "/images/faq-reject-po.png",
		},
		{
			question: "Can I view Purchase Orders I created previously in NetSuite?",
			answer:
				"Yes. Toggle the <b>Show Legacy POs</b> switch on the PO List page. This will show all purchase orders you created in NetSuite prior to using Puff Relay. Legacy POs will have an orange background and will not be editable. You can still see the PO details and download a PDF.",
			imageUrl: "/images/faq-legacy-pos.png",
		},
		{
			question: "How do I see Purchase Orders for a specific vendor?",
			answer:
				'Clicking on the \"Vendor Name\" link on the PO List page will show you the last 5 Purchase orders for that specific Vendor, regardless of who created them. Be sure to toggle on the <b>Show Legacy POs</b> switch to see all previous POs for that vendor created in NetSuite as well and click <b>Clear vendor filter</b> to return to the default list.',
			imageUrl: "/images/faq-po-vendor.png",
		},
		{
			question: "How do I see Purchase Orders by Vendor or Submitter?",
			answer:
				'On the \"PO List\", click on the \"Search all POs\" tab. You can search by Vendor or Submitter using the provided dropdown fields. Please note that this search will show results within the last year.',
			imageUrl: "/images/faq-search-all.png",
		},
	];

	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
				<div className="text-center">
					{/* Welcome Section */}
					<h1 className="text-5xl font-bold text-gray-900 mb-4">
						Welcome to Puff Relay by Built™
					</h1>
					<p className="text-xl text-gray-600 mb-8">
						Purchase Order Management System with NetSuite Integration
					</p>

					{/* Authentication Status */}
					{isAuthenticated ? (
						<div className="space-y-6">
							<div className="bg-white rounded-lg shadow-md p-8 max-w-md mx-auto">
								<p className="text-lg text-gray-700 mb-4">
									Hello,{" "}
									<span className="font-semibold text-blue-600">
										{accounts[0]?.name}
									</span>
									!
								</p>
								<a
									href="/create-po"
									className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
								>
									Go to Create PO
								</a>
							</div>

							{/* FAQ Section */}
							<div className="mt-12">
								<h2 className="text-3xl font-bold text-gray-900 mb-8">
									Frequently Asked Questions
								</h2>
								<div className="max-w-3xl mx-auto space-y-4">
									{faqs.map((faq, index) => (
										<div
											key={index}
											className="bg-white rounded-lg shadow-md overflow-hidden"
										>
											<button
												onClick={() =>
													setOpenFaqIndex(openFaqIndex === index ? null : index)
												}
												className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
											>
												<h3 className="text-lg font-semibold text-gray-900">
													{faq.question}
												</h3>
												<span
													className={`text-2xl text-blue-600 transition-transform ${
														openFaqIndex === index ? "rotate-180" : ""
													}`}
												>
													▼
												</span>
											</button>
											{openFaqIndex === index && (
												<div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
													<p
														className="text-left text-gray-700 mb-4"
														dangerouslySetInnerHTML={{ __html: faq.answer }}
													/>
													{faq.imageUrl && (
														<img
															src={faq.imageUrl}
															alt="FAQ screenshot"
															className="rounded-lg border border-gray-300 max-w-full"
														/>
													)}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						</div>
					) : (
						<div className="bg-white rounded-lg shadow-md p-12 max-w-md mx-auto">
							<p className="text-lg text-gray-700 mb-6">
								Sign in to get started with PuffRelay
							</p>
							<SignInButton />
						</div>
					)}

					{/* Version Updates Link */}
					<div className="mt-12 text-center">
						<a
							href="/version-updates"
							className="text-xs text-gray-500 hover:text-gray-700 underline"
						>
							Version Updates
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};

export { Home };
